import api from "@/api";
import { JSConn } from "@/utilities/js-conn";
import { TemplateWorkOrderTaskLineItem } from "@/store/modules/templateWorkOrderTaskLineItems";
import { find as Rfind, propEq as RpropEq } from "ramda";

export interface TemplateWorkOrderTask {
  description: string;
  id: string;
  name: string;
  sort_order: number;
  template_work_order_id?: string;
  template_work_order_task_line_items: TemplateWorkOrderTaskLineItem[];
}

interface TemplateWorkOrderTaskState {
  templateWorkOrderTasks: TemplateWorkOrderTask[];
  templateWorkOrderTask: TemplateWorkOrderTask | {};
}

const itemDetails = {
  pluralName: "template_work_order_tasks",
  singularName: "template_work_order_task",
};

const emptyTemplateWorkOrderTask: TemplateWorkOrderTask = {
  id: "",
  template_work_order_id: "",
  name: "",
  description: "",
  sort_order: 200,
  template_work_order_task_line_items: [],
};

const apiParamsBuilder = (jsConn: JSConn) => {
  return {
    id: jsConn.formParams.id,
    template_work_order_task: {
      ...jsConn.formParams,
    },
  };
};

const state: TemplateWorkOrderTaskState = {
  templateWorkOrderTasks: [],
  templateWorkOrderTask: {},
};

const mutations = {
  LOAD_TEMPLATE_WORK_ORDER_TASKS(
    state: TemplateWorkOrderTaskState,
    payload: TemplateWorkOrderTask[],
  ) {
    state.templateWorkOrderTasks = payload;
  },
  LOAD_TEMPLATE_WORK_ORDER_TASK(
    state: TemplateWorkOrderTaskState,
    payload: TemplateWorkOrderTask,
  ) {
    state.templateWorkOrderTask = payload;
  },
};

const actions = {
  needTemplateWorkOrderTasks(context: any, jsConn: JSConn) {
    jsConn.apiParams = jsConn.formParams;
    return api.getItems(jsConn, itemDetails).then(jsConn => {
      context.commit("LOAD_TEMPLATE_WORK_ORDER_TASKS", jsConn.response);
      return Promise.resolve(jsConn);
    });
  },
  needTemplateWorkOrderTask(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api.getItem(jsConn, itemDetails).then(jsConn => {
      context.commit("LOAD_TEMPLATE_WORK_ORDER_TASK", jsConn.response);
      return Promise.resolve(jsConn);
    });
  },
  createTemplateWorkOrderTask(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api
      .createItem(jsConn, itemDetails)
      .then(jsConn => {
        context.commit("LOAD_TEMPLATE_WORK_ORDER_TASK", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch(jsConn => {
        return Promise.reject(jsConn);
      });
  },
  updateTemplateWorkOrderTask(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api
      .updateItem(jsConn, itemDetails)
      .then(jsConn => {
        context.commit("LOAD_TEMPLATE_WORK_ORDER_TASK", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch(jsConn => {
        return Promise.reject(jsConn);
      });
  },
  deleteTemplateWorkOrderTask(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);

    return api
      .deleteItem(jsConn, itemDetails)
      .then(jsConn => {
        context.commit(
          "LOAD_TEMPLATE_WORK_ORDER_TASK",
          emptyTemplateWorkOrderTask,
        );
        return Promise.resolve(jsConn);
      })
      .catch(jsConn => {
        return Promise.reject(jsConn);
      });
  },
};

const getters = {
  templateWorkOrderTasks: (state: TemplateWorkOrderTaskState) => {
    return state.templateWorkOrderTasks;
  },
  templateWorkOrderTask: (state: TemplateWorkOrderTaskState) => {
    return state.templateWorkOrderTask;
  },
  templateWorkOrderTasksForTable: (state: TemplateWorkOrderTaskState) => {
    return state.templateWorkOrderTasks;
  },
  templateWorkOrderTaskById: (state: TemplateWorkOrderTaskState) => (
    id: string,
  ) => {
    return Rfind(task => {
      return RpropEq("id", id, task);
    }, state.templateWorkOrderTasks);
  },
};

const templateWorkOrderTasksModule = {
  state,
  mutations,
  actions,
  getters,
};

export default templateWorkOrderTasksModule;
